import { CheckboxContainer, CheckboxText, ChoiceInputRow, Container, Label, Wrapper } from './RightForm.styles';
import { Controller, useFormContext, get } from 'react-hook-form';

import { CheckboxButton } from 'features/common/components/CheckboxButton/CheckBoxButton.styles';
import { PunchupCheckbox } from 'features/common/components/Punchup/primitives/forms/fields/PunchupCheckbox';
import { PunchupInput } from 'features/common/components/Punchup/primitives/forms/fields/PunchupInput';
import { PunchupFieldLabel } from 'features/common/components/Punchup/primitives/forms/fields/PunchupFieldLabel';
import { EVENT_MODE_REGISTRATION } from 'utils/constants';
import { Form } from 'features/common/components/Form';
import { Icon } from 'features/common/components/Icon';
import { Option } from 'features/common/components/Form/types';
import { RightFormProps } from 'features/CustomFields/types';
import { RootState } from 'store';
import { customFieldTypes } from 'utils/types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

export const RightForm = ({ event }: RightFormProps) => {
	const { t } = useTranslation();
	const {
		watch,
		setValue,
		control,
		formState: { errors },
	} = useFormContext();
	const { isPunchup } = useSelector((state: RootState) => state.auth);

	const selectedTickets = watch('ticket_types_ids');
	const selectedType = watch('custom_field.type');
	const choices: { name: string }[] = watch('custom_field.choices');

	const customFieldTypeOptions: Option[] = [
		{ value: 'STRING', label: t('customField-types-text') },
		{ value: 'NUMBER', label: t('customField-types-number') },
		// Uncomment after implementing date input/validations
		// { value: 'DATE', label: t('customField-types-date') },
		{ value: 'DROPDOWN', label: t('customField-types-dropdown') },
		{ value: 'MULTIPLE_CHOICE', label: t('customField-types-multipleChoice') },
	];

	const isChecked = (id: number) => selectedTickets.includes(id);

	const onChange = (id: number) =>
		setValue(
			'ticket_types_ids',
			isChecked(id) ? selectedTickets.filter((ticket: number) => ticket !== id) : [...selectedTickets, id]
		);

	const onAddOption = () => {
		// The "+" only renders once every option is filled (see the render gate below), and the schema
		// enforces non-empty / min / unique on submit — so just append a fresh blank row. (The old
		// imperative setError landed its message on option #0 and fought the schema messaging.)
		setValue('custom_field.choices', [...choices, { name: '' }]);
	};

	const onDeleteOption = (index: number) => {
		setValue(
			'custom_field.choices',
			choices.filter((_, i) => i !== index)
		);
	};

	return (
		<Wrapper isPunchup={isPunchup}>
			{event.mode === EVENT_MODE_REGISTRATION && (
				<Container>
					{isPunchup ? (
						<Form.PunchupSelect
							id="custom_field.type"
							name="custom_field.type"
							label={t('customField-type-label', 'Type')}
							options={customFieldTypeOptions}
							help={t(
								'customField-type-label-help',
								'How attendees answer this question: free text, a number, or a choice from options you set. Dropdown and multiple choice let you list the answers to pick from.'
							)}
						/>
					) : (
						<Form.Select
							id="custom_field.type"
							name="custom_field.type"
							label="Type"
							options={customFieldTypeOptions}
							withDefaultValue={false}
						/>
					)}
				</Container>
			)}

			{[customFieldTypes.MULTIPLE_CHOICE, customFieldTypes.DROPDOWN].includes(selectedType) && (
				<Container>
					{isPunchup ? (
						<PunchupFieldLabel
							as="span"
							label={t('customFields-optionsLabel')}
							help={t(
								'customFields-optionsLabel-help',
								'The answers attendees can choose from. Use the plus button to add another option.'
							)}
						/>
					) : (
						<Label isPunchup={isPunchup}>{t('customFields-optionsLabel')}</Label>
					)}

					{get(errors, 'custom_field.choices')?.message && (
						<span className="punchupFormError">{get(errors, 'custom_field.choices').message as string}</span>
					)}

					{choices.map((choice, index) => (
						<ChoiceInputRow key={index}>
							{isPunchup ? (
								// PunchupInput self-binds via useFormContext (register) — no Controller wrapper. The
								// dark-theme Form.Input rendered white-on-white in the light accordion (its text colour
								// is unconditionally white); PunchupInput reads tokens, so the option text is visible.
								<PunchupInput
									id={`custom_field.choices.${index}.name`}
									name={`custom_field.choices.${index}.name`}
									placeholder={t('customFields-option-placeholder', 'Option')}
								/>
							) : (
								<Controller
									name={`custom_field.choices.${index}.name`}
									control={control}
									defaultValue={choice.name}
									render={({ field, fieldState: { error } }) => (
										<Form.Input id={field.name} errorProp={error?.message} name={field.name} />
									)}
								/>
							)}

							{choices.every((c) => c.name) && index + 1 === choices.length && (
								<Icon iconName="plus" size="M" cursor="pointer" onClick={onAddOption} />
							)}

							{index + 1 !== choices.length && (
								<Icon iconName="trash" size="M" cursor="pointer" onClick={() => onDeleteOption(index)} />
							)}
						</ChoiceInputRow>
					))}
				</Container>
			)}

			<Container marginTop={isPunchup ? '8px' : '32px'} isPunchup={isPunchup}>
				{isPunchup ? (
					<PunchupFieldLabel
						as="span"
						label={t('customFields-pickTickets')}
						help={t(
							'customFields-pickTickets-help',
							'Only buyers of the ticket types you check here will be asked this question.'
						)}
					/>
				) : (
					<Label isPunchup={isPunchup}>{t('customFields-pickTickets')}</Label>
				)}

				{event?.ticket_types?.map((ticket) =>
					isPunchup ? (
						<PunchupCheckbox
							key={ticket.ID}
							checked={isChecked(ticket.ID)}
							onChange={() => onChange(ticket.ID)}
							label={ticket.name}
						/>
					) : (
						<CheckboxContainer key={ticket.ID}>
							<CheckboxButton type="checkbox" checked={isChecked(ticket.ID)} onChange={() => onChange(ticket.ID)} />
							<CheckboxText isPunchup={isPunchup}>{ticket.name}</CheckboxText>
						</CheckboxContainer>
					)
				)}

				{selectedTickets.length === 0 && (
					<span className="punchupFormError">
						{t('customFields-ticketTypes-required', 'Pick at least one ticket type.')}
					</span>
				)}
			</Container>
		</Wrapper>
	);
};
